import { startConnectorOAuth } from "./api";

/**
 * Start a connector OAuth flow and open the provider consent screen in a NEW
 * top-level browser tab.
 *
 * Why a new tab (and not `window.location.assign`): when the app runs embedded
 * in the TiendaNube admin it lives inside an iframe. Google and Meta refuse to
 * render their consent screens inside a frame — they send
 * `X-Frame-Options: DENY` / a restrictive `frame-ancestors` CSP — so a
 * same-window redirect would only navigate the iframe and the consent page is
 * blocked ("refused to connect"). Opening a new top-level tab escapes the
 * frame entirely and leaves the admin surface untouched. After consent the
 * provider redirects to our backend callback (which persists the token) and
 * then to a standalone return page that lands in that new tab; the embedded
 * surface picks up the new credential when the merchant switches back (see the
 * focus-driven refetch + `needs_selection` picker prompt on the Fuentes tab and
 * the private-beta gate).
 *
 * Popup-blocker safety: the blank tab is opened synchronously inside the click
 * gesture, BEFORE the async `/start` round-trip, then navigated once we have
 * the authorize URL. If the popup was blocked we throw `popup_blocked` so the
 * caller can tell the merchant to allow pop-ups — we deliberately do NOT fall
 * back to a top-level redirect: inside the admin iframe that either throws
 * (cross-origin `window.top`) or, if top-navigation were allowed, would blow
 * the merchant out of the admin entirely and strand the picker (which needs the
 * embedded session). New-tab or nothing.
 */
export class PopupBlockedError extends Error {
  constructor() {
    super("popup_blocked");
    this.name = "PopupBlockedError";
  }
}

export async function openConnectorOAuthTab(kind: string): Promise<void> {
  // Synchronous open inside the user gesture — required or popup blockers fire.
  const popup = window.open("", "_blank");
  if (!popup) throw new PopupBlockedError();
  try {
    const res = await startConnectorOAuth(kind);
    if (popup.closed) throw new PopupBlockedError();
    popup.location.replace(res.authorize_url);
  } catch (err) {
    // /start failed (network, auth) or popup gone — close the blank tab so we
    // don't strand an empty window, and let the caller surface the error.
    try {
      popup.close();
    } catch {
      /* ignore */
    }
    throw err;
  }
}
